import React, { useState, useEffect, useRef } from "react";
import { Container, Card, Row, Col } from "react-bootstrap";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import apiConfig from "../credentials/apiConfig";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "./ViewAnalysisPage.scss";
import Loader from "../../components/Loader";

import IconDollar from "../../images/Icon-dollar.png";
import Building from "../../images/icon-building.png";
import IconGraph from "../../images/icon-graph.png";
import IconRocket from "../../images/icon-rocket.png";
import IconPi from "../../images/icon-pi.png";
import IconInvest from "../../images/icon-invest.png";
import IconBrain from "../../images/icon-brain.png";
import IconMarket from "../../images/IconMarket.png";
import IconRisk from "../../images/IconRisk.png";
import IconGrowth from "../../images/IconGrowth.png";
import IconPortfolio from "../../images/IconPortfolio.png";
import PDFViewer from "../dashboard/EngagementSetup/PdfViewer";

import html2pdf from 'html2pdf.js';

// Inline arrow (replaces Font Awesome)
const UpArrow = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 4l6 6h-4v10H10V10H6l6-6z" fill="currentColor" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3v10m0 0l4-4m-4 4L8 9M5 19h14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Mock chart blocks
const DonutChart = () => <div className="chart-placeholder-content donut-chart-visual" />;
const BarChart = () => <div className="chart-placeholder-content bar-chart-visual" />;

const TrendLineChart = ({ labels, series }) => {
  const W = 900, H = 280;
  const M = { t: 20, r: 20, b: 45, l: 42 };
  const innerW = W - M.l - M.r, innerH = H - M.t - M.b;
  const maxY = 10, minY = 0;

  const x = (i) => M.l + (i / (labels.length - 1)) * innerW;
  const y = (v) => M.t + (maxY - v) / (maxY - minY) * innerH;
  const linePath = (values) =>
    values.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`).join(" ");

  return (
    <div className="trend-chart-wrap">
      <div className="legend">
        {series.map((s) => (
          <span key={s.key}>
            <i className={`dot-swatch ${s.key}`} />
            {s.name}
          </span>
        ))}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="trend-svg" role="img" aria-label="Market Intelligence Score Trends">
        <defs>
          <linearGradient id="bgGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e6fcf5" />
            <stop offset="100%" stopColor="#ffffff" />
          </linearGradient>
        </defs>

        {/* soft rounded background */}
        <rect x="10" y="10" width={W - 20} height={H - 20} rx="12" ry="12" fill="url(#bgGrad)" />

        {/* horizontal grid + Y labels */}
        {[0, 2, 4, 6, 8, 10].map((g, i) => (
          <g key={i}>
            <line x1={M.l} y1={y(g)} x2={M.l + innerW} y2={y(g)} className="grid" />
            <text x={M.l - 10} y={y(g) + 4} className="y-label">{g}</text>
          </g>
        ))}

        {/* X labels */}
        {labels.map((lbl, i) => (
          <text key={i} x={x(i)} y={H - 12} className="x-label">{lbl}</text>
        ))}

        {/* lines + dots */}
        {series.map((s) => (
          <g key={s.key} className={`series ${s.key}`}>
            <path d={linePath(s.values)} className="line" fill="none" />
            {s.values.map((v, i) => (
              <circle key={i} cx={x(i)} cy={y(v)} r="4" className="dot" />
            ))}
          </g>
        ))}
      </svg>
    </div>
  );
};

function ViewAnalysisPage() {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysisData, setAnalysisData] = useState({
    totalPortfolioValue: "$169.6M",
    activeCompanies: 42,
    marketValuation: "$195.2M",
    exitOpportunities: 4,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [apiData, setApiData] = useState(null);

  // Create a ref for the Portfolio Holdings Overview section
  const holdingsSectionRef = useRef(null);

  // Function to handle PDF generation and viewing
  const handleDownloadPDF = async () => {
    try {
      setIsDownloading(true);
      setPdfTitle(`${apiData.clientName} Investment Analysis`);

      // Generate PDF from the current page data
      const pdfBlob = await generatePDF();

      // Set the PDF blob and show the viewer
      setPdfBlob(pdfBlob);
      setShowPdfViewer(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF report: ' + error.message);
    } finally {
      setIsDownloading(false);
    }
  };



  // Fetch data from API
  const fetchInvestmentScheduleData = async (id) => {
    setIsLoading(true);
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.get(
        `${apiConfig.apiBaseURL}/getInvestmentSchedule/${id}`,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Authorization": `Bearer ${authToken}`
          }
        }
      );

      if (response.data && response.data.success) {
        setApiData(response.data.investmentSchedule);
        
        // Update analysisData with API response if needed
        // setAnalysisData(response.data.data);
      } else {
        toast.error(response.data?.message || "Failed to fetch investment schedule data");
      }
    } catch (error) {
      console.error("Error fetching investment schedule data:", error);
      toast.error(error.response?.data?.message || "Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  // Get data from navigation state or fetch from API
  useEffect(() => {
      fetchInvestmentScheduleData(id);
  }, [id]);

  const [showModal, setShowModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Add state for PDF viewer
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfTitle, setPdfTitle] = useState("");

  const openDetails = (row) => {
    // You can map your real data here; using sensible defaults for now
    const fallback = {
      overview: {
        website: "https://example.com  ",
        founded: "2022",
        employees: "220",
        sector: row.i || "Healthcare Technology",
        subSector: "Digital Health",
        blurb:
          `${row?.n || "Company"} is a leading provider of innovative solutions in the healthcare space.`,
      },
      metrics: {
        totalFunding: "100M",
        lastRound: "Series B",
        lastInvestment: "33M",
        leadInvestor: "Sequoia Capital",
      },
      fundingHistory: [
        { when: "June 2024", round: "50M Series C", meta: "Investors: Tiger Global, Sequoia Capital" },
        { when: "March 2023", round: "25M Series B", meta: "Investors: Accel, Lightspeed" },
        { when: "Nov 2021", round: "10M Series A", meta: "Investors: YC, Angel Investors" },
      ],
      competitors: ["Competitor A", "Competitor B", "Competitor C"],
      recommendation:
        "Based on recent momentum, the company shows strong growth potential with consistent funding rounds and expanding market presence.",
    };

    setSelectedCompany({ ...row, ...fallback });
    setShowModal(true);
  };
  const closeDetails = () => setShowModal(false);

  // Generate PDF from full portfolio analysis data
  const generatePDF = async () => {
    try {
      // Create container element for PDF content
      const container = document.createElement("div");
      container.className = "pdf-generation-container";

      // Add CSS styles for PDF
      container.innerHTML = `
        <style>
          .pdf-generation-container {
            width: 100%;
            padding: 20px;
            font-family: Arial, sans-serif;
            background: white;
          }
          .pdf-generation-container h1 {
            font-size: 24px;
            font-weight: bold;
            margin: 0 0 20px 0;
            text-align: center;
            color: #333;
          }
          .pdf-generation-container h2 {
            font-size: 18px;
            font-weight: bold;
            margin: 20px 0 10px 0;
            color: #444;
            border-bottom: 2px solid #eee;
            padding-bottom: 5px;
          }
          .pdf-generation-container h3 {
            font-size: 16px;
            font-weight: bold;
            margin: 15px 0 8px 0;
            color: #555;
          }
          .pdf-generation-container p {
            font-size: 12px;
            line-height: 1.4;
            margin: 8px 0;
            color: #666;
          }
          .pdf-generation-container table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
            font-size: 11px;
          }
          .pdf-generation-container th,
          .pdf-generation-container td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            vertical-align: top;
          }
          .pdf-generation-container th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          .pdf-generation-container .section-spacing {
            margin: 25px 0;
          }
          .pdf-generation-container .executive-summary {
            background-color: #f8f9fa;
            padding: 15px;
            border-radius: 5px;
            margin: 15px 0;
          }
        </style>
      `;

      // Build PDF content
      let pdfContent = `
        <div class="pdf-generation-container">
          <h1>Portfolio Analysis Report</h1>
          
          <div class="executive-summary">
            <h2>Executive Summary</h2>
            <p>${apiData?.executiveSummary || 'Portfolio analysis and market intelligence report.'}</p>
          </div>

          <div class="section-spacing">
            <h2>Audit Considerations</h2>
            
            <p><strong>Risks of material misstatement:</strong> ${apiData?.riskAuditIntelligence?.riskOfMaterialMisstatement}</p>

            <p><strong>High risk areas:</strong>${apiData?.riskAuditIntelligence?.highRiskValuationAreas}</p>

            <p><strong>Company valuation:</strong>${apiData?.riskAuditIntelligence?.companyValuation}</p>

            <p><strong>Suggested audit procedures:</strong> ${apiData?.riskAuditIntelligence?.suggestedAuditProcedures}</p>

            <p><strong>Economic impacts:</strong> ${apiData?.riskAuditIntelligence?.economicImpacts}</p>
          </div>

          

          <div class="section-spacing">
            <h2>Portfolio Holdings Overview</h2>
            <table>
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Investment Type</th>
                  <th>Industry</th>
                  <th>Current Value</th>
                  <th>Prior Year Value</th>
                  <th>Change</th>
                  <th>Market Intel</th>
                </tr>
              </thead>
              <tbody>
      `;

      // Add holdings data
      if (apiData?.portfolioHoldingsOverview) {
        apiData.portfolioHoldingsOverview.forEach(company => {
          pdfContent += `
            <tr>
              <td>${company.companyName}</td>
              <td>${company.investmentType}</td>
              <td>${company.industry}</td>
              <td>${company.currentValue}</td>
              <td>${company.priorYearValue}</td>
              <td>${company.valueChange}</td>
              <td>${company.marketIntel}</td>
            </tr>
          `;
        });
      }

      pdfContent += `
              </tbody>
            </table>
          </div>

          <div class="section-spacing">
            <h2>Market Intelligence & Actions</h2>`

      // Add dynamic market intelligence actions
      if (apiData?.marketIntelligenceActions) {
        apiData.marketIntelligenceActions.forEach(action => {
          const getDisplayTitle = (type) => {
            switch(type) {
              case 'market_trend': return 'Market Trends';
              case 'risk_alert': return 'Risk Alert';
              case 'growth_opportunity': return 'Growth Opportunity';
              case 'portfolio_balance': return 'Portfolio Balance';
              default: return 'Intelligence';
            }
          };

          pdfContent += `
            <h3>${getDisplayTitle(action.type)}</h3>
            <p>${action.title}</p>
            <p>${action.description}</p>
            <p><strong>Status:</strong> ${action.label}</p>
            ${action.confidence ? `<p><strong>Confidence:</strong> ${action.confidence}</p>` : ''}
            ${action.riskLevel ? `<p><strong>Risk Level:</strong> ${action.riskLevel}</p>` : ''}
            ${action.growthScore ? `<p><strong>Growth Score:</strong> ${action.growthScore}/10</p>` : ''}
            ${action.diversityScore ? `<p><strong>Diversity Score:</strong> ${action.diversityScore}/10</p>` : ''}
          `;
        });
      }

      pdfContent += `
            <h3>Recommended Actions</h3>
            <table>
              <thead>
                <tr>
                  <th>Timeframe</th>
                  <th>Action Title</th>
                  <th>Recommendation</th>
                  <th>Priority</th>
                  <th>Action Label</th>
                </tr>
              </thead>
              <tbody>`

      // Add dynamic recommended actions
      if (apiData?.recommendedActions) {
        apiData.recommendedActions.forEach(action => {
          pdfContent += `
                <tr>
                  <td>${action.timeframe}</td>
                  <td>${action.title}</td>
                  <td>${action.recommendation}</td>
                  <td>${action.priority}</td>
                  <td>${action.actionLabel}</td>
                </tr>
          `;
        });
      }

      pdfContent += `
              </tbody>
            </table>
          </div>

          <div class="section-spacing">
            <h2>Market Intelligence & Competitive Landscape</h2>
            
            <h3>Sector Funding Trends</h3>`
            
            // Add dynamic sector funding trends
            if (apiData?.sectorFundingTrends) {
              apiData.sectorFundingTrends.forEach(trend => {
                pdfContent += `
            <p><strong>${trend.sector}:</strong></p>
            <p>• Total Funding: ${trend.totalFunding}</p>
            <p>• Year-over-Year Growth: ${trend.yoyGrowth}</p>
            
                `;
              });
            } else {
              // Fallback data if no dynamic data is available
              pdfContent += `
            <p><strong>Healthcare Technology:</strong></p>
            <p>• Total Funding: $12.5B (2024)</p>
            <p>• Year-over-Year Growth: +23%</p>
           
            
            <p><strong>SAAS/Technology:</strong></p>
            <p>• Total Funding: $8.3B (2024)</p>
            <p>• Year-over-Year Growth: +15%</p>
            
            <p><strong>Digital Media:</strong></p>
            <p>• Total Funding: $3.2B (2024)</p>
            <p>• Year-over-Year Growth: -5% (Declining)</p>
              `;
            }
            
            pdfContent += `
            
            <h3>Market Position Analysis</h3>`

            // Add dynamic market position analysis
            if (apiData?.marketPositionAnalysis) {
              pdfContent += `
            <p><strong>Portfolio Strength:</strong> ${apiData.marketPositionAnalysis.portfolioStrength}</p>
            <p><strong>Market Timing:</strong> ${apiData.marketPositionAnalysis.marketTiming}</p>
            <p><strong>Competitive Edge:</strong> ${apiData.marketPositionAnalysis.competitiveEdge}</p>
              `;
            } else {
              // Fallback data
              pdfContent += `
            <p><strong>Portfolio Strength:</strong> 75% of portfolio companies are in high-growth sectors</p>
            <p><strong>Market Timing:</strong> Healthcare tech investments align with market trends</p>
            <p><strong>Competitive Edge:</strong> Early-stage positions in emerging sub-sectors</p>
              `;
            }

            pdfContent += `
            
            <h3>Key Market Players & Competitors</h3>
            <table>
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Market Position</th>
                  <th>Valuation</th>
                  <th>Status</th>
                  <th>Competitive Impact</th>
                </tr>
              </thead>
              <tbody>`

      // Add dynamic key market players data
      if (apiData?.keyMarketPlayersAndCompetitors) {
        apiData.keyMarketPlayersAndCompetitors.forEach(player => {
          pdfContent += `
                <tr>
                  <td>${player.companyName}</td>
                  <td>${player.marketPosition}</td>
                  <td>${player.valuation}</td>
                  <td>${player.status}</td>
                  <td>${player.competitiveImpact}</td>
                </tr>
          `;
        });
      } else {
        // Fallback data if no dynamic data is available
        pdfContent += `
                <tr>
                  <td>Teladoc Health</td>
                  <td>Market Leader in Telehealth</td>
                  <td>$8.5B</td>
                  <td>IPO (2015)</td>
                  <td>Established market leader setting industry standards</td>
                </tr>
                <tr>
                  <td>Ro</td>
                  <td>Direct-to-Consumer Healthcare</td>
                  <td>$5B</td>
                  <td>Series D ($500M)</td>
                  <td>Innovative direct-consumer model disrupting traditional healthcare</td>
                </tr>
                <tr>
                  <td>Hims & Hers</td>
                  <td>Telehealth Platform</td>
                  <td>$1.6B</td>
                  <td>IPO (2021)</td>
                  <td>Consumer-focused telehealth platform with strong brand recognition</td>
                </tr>
        `;
      }

      pdfContent += `
              </tbody>
            </table>
          </div>


          <div class="section-spacing">
            <h2>Risk Assessment with Market Context</h2>
            
            <table>
              <thead>
                <tr>
                  <th>Risk Factor</th>
                  <th>Severity</th>
                  <th>Benchmark</th>
                  <th>Mitigation Strategy</th>
                </tr>
              </thead>
              <tbody>`

      // Add dynamic risk assessment data
      if (apiData?.riskAssessmentWithMarketContext) {
        apiData.riskAssessmentWithMarketContext.forEach(risk => {
          pdfContent += `
                <tr>
                  <td>${risk.riskFactor}</td>
                  <td>${risk.severity}</td>
                  <td>${risk.benchmark}</td>
                  <td>${risk.mitigationStrategy}</td>
                </tr>
          `;
        });
      } else {
        // Fallback data if no dynamic data is available
        pdfContent += `
                <tr>
                  <td>Industry Concentration: 73.5% in healthcare sector</td>
                  <td>Medium</td>
                  <td>Tracxn Benchmark: 45–55% recommended</td>
                  <td>Diversification: Target enterprise software deals</td>
                </tr>
                <tr>
                  <td>Valuation Risk: Limited observable market data</td>
                  <td>Medium</td>
                  <td>3 companies below Tracxn market multiples</td>
                  <td>Value Creation: Operational improvements</td>
                </tr>
                <tr>
                  <td>Market Risk: Digital media sector declining</td>
                  <td>High</td>
                  <td>On3 Media in declining sector per Tracxn</td>
                  <td>Exit Planning: 2 companies ready for exit</td>
                </tr>
        `;
      }

      pdfContent += `
              </tbody>
            </table>
          </div>
        </div>
      `;

      container.innerHTML += pdfContent;

      // Add to document temporarily
      document.body.appendChild(container);

      // Generate dynamic filename with client name
      const clientName = apiData?.clientName || 'Client';
      const filename = `${clientName} Investment Analysis.pdf`;
      // PDF generation options
      const pdfOptions = {
        margin: [15, 15, 15, 15],
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 1.2,
          useCORS: true,
          letterRendering: true
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait'
        },
        pagebreak: {
          mode: ["avoid-all", "css", "legacy"], // 👈 handle breaks
          before: ".page-break", // force break before these
          after: ".page-break-after", // break after these
          avoid: ".avoid-break", // don't split these
        },
      };

      // Generate PDF
      const pdfBlob = await html2pdf().from(container).set(pdfOptions).outputPdf('blob');

      // Clean up
      document.body.removeChild(container);

      return pdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      throw new Error("Failed to generate PDF report");
    }
  };

  // Generate PDF from holdings data only
  const generateHoldingsPDF = async () => {
    try {
      // Create container element for PDF content
      const container = document.createElement("div");
      container.className = "pdf-generation-container";

      // Add CSS styles for PDF
      container.innerHTML = `
        <style>
          .pdf-generation-container {
            width: 100%;
            padding: 20px;
            font-family: Arial, sans-serif;
            background: white;
          }
          .pdf-generation-container h1 {
            font-size: 24px;
            font-weight: bold;
            margin: 0 0 20px 0;
            text-align: center;
            color: #333;
          }
          .pdf-generation-container p {
            font-size: 12px;
            line-height: 1.4;
            margin: 8px 0 20px 0;
            color: #666;
            text-align: center;
          }
          .pdf-generation-container table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
            font-size: 11px;
          }
          .pdf-generation-container th,
          .pdf-generation-container td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            vertical-align: top;
          }
          .pdf-generation-container th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
        </style>
      `;

      // Build PDF content for holdings only
      let pdfContent = `
        <div class="pdf-generation-container">
          <h1>Portfolio Holdings Overview</h1>
          <p>Detailed breakdown of all investments with market intelligence</p>
          
          <table>
            <thead>
              <tr>
                <th>Company Name</th>
                <th>Investment Type</th>
                <th>Industry</th>
                <th>Current Value</th>
                <th>Prior Year Value</th>
                <th>Change</th>
                <th>Market Intel</th>
              </tr>
            </thead>
            <tbody>
      `;

      // Add holdings data
      if (apiData?.portfolioHoldingsOverview) {
        apiData.portfolioHoldingsOverview.forEach(company => {
          pdfContent += `
            <tr>
              <td>${company.companyName}</td>
              <td>${company.investmentType}</td>
              <td>${company.industry}</td>
              <td>${company.currentValue}</td>
              <td>${company.priorYearValue}</td>
              <td>${company.valueChange}</td>
              <td>${company.marketIntel}</td>
            </tr>
          `;
        });
      }

      pdfContent += `
            </tbody>
          </table>
        </div>
      `;

      container.innerHTML += pdfContent;

      // Add to document temporarily
      document.body.appendChild(container);

      // Generate dynamic filename with client name
      const clientName = apiData?.clientName || 'Client';
      const filename = `${clientName} Investment Analysis.pdf`;

      // PDF generation options
      const pdfOptions = {
        margin: [15, 15, 15, 15],
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 1.2,
          useCORS: true,
          letterRendering: true
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'landscape'
        }
      };

      // Generate PDF
      const pdfBlob = await html2pdf().from(container).set(pdfOptions).outputPdf('blob');

      // Clean up
      document.body.removeChild(container);

      return pdfBlob;
    } catch (error) {
      console.error("Error generating holdings PDF:", error);
      throw new Error("Failed to generate holdings PDF");
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="container-fluid py-4">
        <Loader text="Loading investment schedule data..." />
      </div>
    );
  }

  // Safer, case-insensitive, and avoids "inactive" ⇒ "active" collision
const getMarketIntelClass = (value) => {
  const v = String(value || "").trim().toLowerCase();

  // exact keywords we care about
  if (/\bactive\b/.test(v)) return "active";
  if (/\bgrowing\b/.test(v)) return "growing";
  if (/\bpivoting\b/.test(v)) return "pivoting";
  if (/\bhot\b/.test(v)) return "hot";
  if (/\bnew\b/.test(v) || /new investment/.test(v)) return "new";
  if (/\binactive\b/.test(v)) return "inactive"; // optional
  return "neutral";
};

  return (
    <Container fluid className="view-analysis-page py-4">
      {/* Debug: Display API data */}
      {apiData && (
        <div style={{ display: 'none' }}>
          <pre>{JSON.stringify(apiData, null, 2)}</pre>
        </div>
      )}
      
      <div className="d-flex justify-content-end mb-3 gap-3">
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate('/investment-schedules')}
        >
          Back to List
        </button>
        <button
          className="btn btn-primary btn-download-pdf pdf-exclude"
          onClick={handleDownloadPDF}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <>
              <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
              Generating PDF...
            </>
          ) : (
            <>
              <DownloadIcon /> Download as PDF
            </>
          )}
        </button>
      </div>


      <Card className="summary-card mt-3">
        <Card.Body className="p-4">
          {/* Executive Summary */}
          <div className="mb-4">
            <h4 className="section-title mb-1">Executive Summary</h4>
            <p className="section-subtitle mb-4">{apiData?.executiveSummary}</p>
          </div>
        </Card.Body>
      </Card>

      {/* Portfolio Overview Section - Separate Card */}
      {apiData?.strategicPortfolioInsights && (
        <Card className="summary-card mt-3">
          <Card.Body className="p-4">
            <div className="mb-4">
              <h4 className="section-title mb-1">Portfolio Overview</h4>
              <p className="section-subtitle mb-4">{apiData?.strategicPortfolioInsights}</p>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Market Intelligence & Actions */}
      <Card className="market-intelligence-section mt-4">
        <Card.Body className="p-4">
          <div className="section-header d-flex align-items-center mb-3">
            <img className="prefix-icon me-2" src={IconBrain} alt="" />
            <h4 className="section-title mb-0">Market Intelligence &amp; Actions</h4>
          </div>
          <p className="section-subtitle text-muted mb-4">
            AI-powered insights and recommended actions for portfolio optimization
          </p>

          <Row className="g-3">
            {apiData?.marketIntelligenceActions?.map((action, index) => {
              // Helper function to get card styling based on type
              const getCardStyling = (type) => {
                switch(type) {
                  case 'market_trend':
                    return {
                      backgroundColor: "#f8f9ff",
                      iconBg: "#22c55e",
                      icon: IconMarket,
                      badgeBg: "#22c55e"
                    };
                  case 'risk_alert':
                    return {
                      backgroundColor: "#fffbeb",
                      iconBg: "#f59e0b",
                      icon: IconRisk,
                      badgeBg: "#f59e0b"
                    };
                  case 'growth_opportunity':
                    return {
                      backgroundColor: "#ecfdf5",
                      iconBg: "#10b981",
                      icon: IconGrowth,
                      badgeBg: "#10b981"
                    };
                  case 'portfolio_balance':
                    return {
                      backgroundColor: "#eff6ff",
                      iconBg: "#3b82f6",
                      icon: IconPortfolio,
                      badgeBg: "#3b82f6"
                    };
                  default:
                    return {
                      backgroundColor: "#f8f9ff",
                      iconBg: "#6b7280",
                      icon: IconBrain,
                      badgeBg: "#6b7280"
                    };
                }
              };

              // Helper function to get display title based on type
              const getDisplayTitle = (type) => {
                switch(type) {
                  case 'market_trend':
                    return 'Market Trends';
                  case 'risk_alert':
                    return 'Risk Alert';
                  case 'growth_opportunity':
                    return 'Growth Opportunity';
                  case 'portfolio_balance':
                    return 'Portfolio Balance';
                  default:
                    return 'Intelligence';
                }
              };

              const styling = getCardStyling(action.type);
              
              return (
                <Col md={3} sm={6} key={index} className="d-flex align-items-stretch">
                  <Card className="w-100 border-0 shadow-sm d-flex" style={{ backgroundColor: styling.backgroundColor, minHeight: '300px' }}>
                    <Card.Body className="p-4 d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <div className="rounded-3 p-2 me-3" style={{ backgroundColor: styling.iconBg }}>
                          <img src={styling.icon} alt="" width="20" height="20" />
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">{getDisplayTitle(action.type)}</h6>
                          <small className="text-muted">{action.title}</small>
                        </div>
                      </div>
                      <p className="text-dark mb-3 flex-grow-1" style={{ fontSize: "14px", lineHeight: "1.4" }}>
                        {action.description}
                      </p>
                      <div className="d-flex flex-column gap-2 mt-auto">
                        <span className="badge text-white fw-medium px-3 py-2" style={{ backgroundColor: styling.badgeBg, fontSize: "11px" }}>
                          {action.label?.toUpperCase()}
                        </span>
                        {action.confidence && (
                          <small className="text-muted">Confidence: {action.confidence}</small>
                        )}
                        {action.riskLevel && (
                          <small className="text-muted">Risk Level: {action.riskLevel}</small>
                        )}
                        {action.growthScore && (
                          <small className="text-muted">Growth Score: {action.growthScore}/10</small>
                        )}
                        {action.diversityScore && (
                          <small className="text-muted">Diversity Score: {action.diversityScore}/10</small>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {/* Recommended Actions */}
          <Card className="market-intelligence-section mt-4">
            <Card.Body className="p-4">
              <div className="section-header">
                <h4 className="section-title mb-0">Recommended Actions</h4>
              </div>

              <Card className="actions-section mt-3">
                <Card.Body>
                  {apiData?.recommendedActions?.map((action, idx) => {
                    // Helper function to get tone based on priority and timeframe
                    const getTone = (priority, timeframe) => {
                      if (priority === "High" || timeframe === "Immediate") return "critical";
                      if (priority === "Medium" || timeframe === "Medium-term") return "warning";
                      return "success";
                    };

                    const tone = getTone(action.priority, action.timeframe);

                    return (
                      <div key={action._id || idx} className={`action-card ${tone}`}>
                        <div className={`action-number ${tone}`}>{idx + 1}</div>
                        <div className="action-content">
                          <div className="action-header d-flex justify-content-between align-items-start">
                            <div className="action-title">
                              <strong>{action.timeframe}:</strong> {action.title}
                            </div>
                            <span className="priority-label">Priority: {action.priority}</span>
                          </div>
                          <div className="action-desc">{action.recommendation}</div>
                          <div className="action-footer">
                            <button className={`action-btn ${tone}`}>{action.actionLabel}</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </Card.Body>
              </Card>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* Portfolio Holdings Overview */}
      <Card className="holdings-card mt-4" ref={holdingsSectionRef}>
        <Card.Body>
          <div className="holdings-header">
            <div>
              <h4 className="section-title mb-1">Portfolio Holdings Overview</h4>
              <p className="section-subtitle mb-0">Detailed breakdown of all investments with market intelligence</p>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table holdings-table mb-0">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Investment Type</th>
                  <th>Industry</th>
                  <th>Current Value</th>
                  <th>Prior Year Value</th>
                  <th>Change</th>
                  <th className="mi-col">Market Intel</th>
                </tr>
              </thead>
              <tbody>
                {apiData?.portfolioHoldingsOverview?.map((company, idx) => (
                  <tr key={idx}>
                    <td>{company.companyName}</td>
                    <td>{company.investmentType}</td>
                    <td>{company.industry}</td>
                    <td>{company.currentValue}</td>
                    <td>{company.priorYearValue}</td>
                    <td className={
                      company.valueChange.startsWith("+") ? "chg-pos" : 
                      company.valueChange === "No Change" ? "chg-neutral" : "chg-info"
                    }>{company.valueChange}</td>
                    <td className="mi-col">
                      <span className={`mi-badge ${getMarketIntelClass(company.marketIntel)}`}>
   {company.marketIntel}
</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      


      {/* PDF Viewer Modal */}
      <PDFViewer
        show={showPdfViewer}
        onHide={() => setShowPdfViewer(false)}
        pdfBlob={pdfBlob}
        title={pdfTitle}
      />
    </Container>
  );
}

export default ViewAnalysisPage;